import _ from 'underscore';
import { useEffect, useContext, useState, useCallback, useRef } from 'react';

import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useLogRecommendationsApiError } from '@bingads-webui-campaign-react/copilot-common';

import { RecommendationContext } from './recommendation-context';
import { getOnlineBusinessNameRecommendations, getOnlineTextRecommendations, getOfflineRecommendations, convertOnlineTextRecommendationToOfflineFormat, getSuggestionIdsFromOfflineRecommendations, getEmptyRecommendationsContext, getOnlineNativeAndSearchTextRecommendations } from './utils';
import { useImageRecommendations } from './use-image-recommendations';
import { useCopilotRecommendations } from './use-copilot-recommendations';

export function useRecommendations({
  cid,
  aid,
  campaignId,
  finalUrl,
  permissions,
  instrumentation,
  isSignup = false,
  isDisplayPlus = false,
  isMMA = false,
  enableCopilotRecommendations = true,
  skipBusinessNameRecommendation = false,
}) {
  const activity = useContext(ActivityContext);
  const logRecommendationsAPIError = useLogRecommendationsApiError(activity);
  const [, setRecommendationContext, reportRecommendations] = useContext(RecommendationContext);
  const [offlineRecommendations, setOfflineRecommendations] = useState(null);
  const [isOfflineRecommendationsFetched, setIsOfflineRecommendationsFetched] = useState(false);

  const {
    suggestionPoller,
    initImageRecommendationForUrl,
    setOfflineImageRecommendations,
  } = useImageRecommendations({
    cid,
    aid,
    campaignId,
    permissions,
    instrumentation,
    enableCopilotRecommendations,
  });

  const resetRecommendations = useCallback((newFinalUrl, recommendationSource) => {
    setRecommendationContext(getEmptyRecommendationsContext(newFinalUrl, recommendationSource));
  }, [setRecommendationContext]);

  const {
    handleCopilotBannerFetch,
    handleCopilotBannerFetchSucceeded,
    handleCopilotBannerFetchFailed,
  } = useCopilotRecommendations({
    resetRecommendations,
    isSignup,
    isDisplayPlus,
    isMMA,
  });

  const fetchOnlineTextRecommendationsAsyncToken = useRef(null);
  const fetchOnlineBusinessNameRecommendationsAsyncToken = useRef(null);

  const cancelFetchTextAndBusinessNameOnlineRecommendations = useCallback(() => {
    if (fetchOnlineTextRecommendationsAsyncToken.current) {
      fetchOnlineTextRecommendationsAsyncToken.current.cancel();
      fetchOnlineTextRecommendationsAsyncToken.current = null;
    }

    if (fetchOnlineBusinessNameRecommendationsAsyncToken.current) {
      fetchOnlineBusinessNameRecommendationsAsyncToken.current.cancel();
      fetchOnlineBusinessNameRecommendationsAsyncToken.current = null;
    }
  }, []);

  const fetchTextAndBusinessNameOnlineRecommendations = useCallback((currentFinalUrl) => {
    cancelFetchTextAndBusinessNameOnlineRecommendations();

    if (enableCopilotRecommendations && !skipBusinessNameRecommendation) {
      fetchOnlineBusinessNameRecommendationsAsyncToken.current = createAsyncToken(getOnlineBusinessNameRecommendations({
        cid, aid, campaignId, finalUrl: currentFinalUrl,
      })).on({
        onSuccess: (businessNameRecommendation) => {
          if (businessNameRecommendation.BusinessName) {
            setRecommendationContext({
              businessName: { Text: businessNameRecommendation.BusinessName, SuggestionId: null },
            });
          }
        },
        onError: logRecommendationsAPIError,
      });
    }

    // fetch online text recommendations
    if (!enableCopilotRecommendations) {
      if (isDisplayPlus) {
        fetchOnlineTextRecommendationsAsyncToken.current = createAsyncToken(getOnlineNativeAndSearchTextRecommendations({
          cid, aid, campaignId, finalUrl: currentFinalUrl,
        }));
      } else {
        fetchOnlineTextRecommendationsAsyncToken.current = createAsyncToken(getOnlineTextRecommendations({
          cid, aid, campaignId, finalUrl: currentFinalUrl,
        }));
      }
      fetchOnlineTextRecommendationsAsyncToken.current.on({
        onSuccess: (textRecommendations) => {
          const convertedRecommendations = convertOnlineTextRecommendationToOfflineFormat(textRecommendations.value);
          setRecommendationContext({
            headlines: convertedRecommendations.headlines,
            longHeadlines: convertedRecommendations.longHeadlines,
            descriptions: convertedRecommendations.descriptions,
          });
          const recommendationsToLog = [
            ...convertedRecommendations.headlines,
            ...convertedRecommendations.longHeadlines,
            ...convertedRecommendations.descriptions,
          ];
          reportRecommendations({
            returned: _.pluck(recommendationsToLog, 'SuggestionId'),
          });
        },
        onError: logRecommendationsAPIError,
      });
    }
  }, [enableCopilotRecommendations, cancelFetchTextAndBusinessNameOnlineRecommendations, cid, aid, campaignId, reportRecommendations, setRecommendationContext, logRecommendationsAPIError, isDisplayPlus]);

  const getOfflineRecommendationsForUrl = useCallback((currentFinalUrl) => {
    if (!offlineRecommendations) {
      return null;
    }

    const recommendationForFinalUrl = _.find(offlineRecommendations, recommendation => recommendation.FinalUrl === currentFinalUrl);
    return (recommendationForFinalUrl && recommendationForFinalUrl.AssetSuggestions && recommendationForFinalUrl.AssetSuggestions[0]);
  }, [offlineRecommendations]);

  const populateTextAndBusinessNameOfflineRecommendations = useCallback((offlineRecommendationsForUrl) => {
    if (enableCopilotRecommendations) {
      setRecommendationContext({
        businessName: offlineRecommendationsForUrl.BusinessName && offlineRecommendationsForUrl.BusinessName.Text,
      });
    } else {
      setRecommendationContext({
        headlines: offlineRecommendationsForUrl.Headlines,
        longHeadlines: offlineRecommendationsForUrl.LongHeadlines,
        descriptions: offlineRecommendationsForUrl.Descriptions,
        businessName: offlineRecommendationsForUrl.BusinessName && offlineRecommendationsForUrl.BusinessName.Text,
      });
    }
  }, [enableCopilotRecommendations, setRecommendationContext]);

  const determineRecommendationSource = useCallback((offlineRecommendationsFetched, offlineRecommendationForUrlExist) => {
    if (enableCopilotRecommendations) {
      return 'copilot';
    }
    if (offlineRecommendationsFetched) {
      return offlineRecommendationForUrlExist ? 'offline' : 'online';
    }
    return 'undetermined';
  }, [enableCopilotRecommendations]);

  // Fetch offline recommendations on mount
  useEffect(() => {
    if (!cid || !aid) {
      return;
    }
    getOfflineRecommendations({
      cid,
      aid,
    }).then((recommendations) => {
      setOfflineRecommendations(recommendations.value);
      if (!_.isNull(recommendations.value)) {
        const finalUrls = _.pluck(recommendations.value, 'FinalUrl');
        setRecommendationContext({
          finalUrls,
        });
      }
    }).catch(logRecommendationsAPIError)
      .finally(() => {
        setIsOfflineRecommendationsFetched(true);
      });
  }, [cid, aid, setRecommendationContext, logRecommendationsAPIError]);

  // Initialize image recommendation and trigger online image suggestion every finalUrl change.
  // Online recommendation is used by website images
  // We should always invoke it on URL change to be able to populate asset library -> website images panel
  useEffect(() => {
    if (!cid || !aid || !campaignId) {
      return;
    }
    initImageRecommendationForUrl(finalUrl);
  }, [aid, campaignId, cid, finalUrl, initImageRecommendationForUrl]);

  // Triggered on finalUrl change OR isOfflineRecommendationsFetched: Processes conditions and determine triggering of text,
  // business name and/or image recommendations
  useEffect(() => {
    cancelFetchTextAndBusinessNameOnlineRecommendations();

    const offlineRecommendationsForFinalUrl = getOfflineRecommendationsForUrl(finalUrl);
    const recommendationSource = determineRecommendationSource(isOfflineRecommendationsFetched, offlineRecommendationsForFinalUrl);
    if (!enableCopilotRecommendations) {
      resetRecommendations(finalUrl, recommendationSource);
    } // useCopilotRecommendations will reset recommendation context for copilot recommendations

    // We need to wait for offline recommendation to be fetched to determine if we should populate online or offline recommendation
    if (!_.isEmpty(finalUrl) && (isOfflineRecommendationsFetched)) {
      if (offlineRecommendationsForFinalUrl) {
        populateTextAndBusinessNameOfflineRecommendations(offlineRecommendationsForFinalUrl);
        if (!enableCopilotRecommendations) {
          setOfflineImageRecommendations(offlineRecommendationsForFinalUrl);
          reportRecommendations({ returned: getSuggestionIdsFromOfflineRecommendations(offlineRecommendationsForFinalUrl) });
        }
        // TODO: tailered report when copilot recommendations is on
      } else if (cid && aid) {
        fetchTextAndBusinessNameOnlineRecommendations(finalUrl);
      }
    }
  }, [
    aid,
    cid,
    enableCopilotRecommendations,
    fetchTextAndBusinessNameOnlineRecommendations,
    getOfflineRecommendationsForUrl,
    resetRecommendations,
    cancelFetchTextAndBusinessNameOnlineRecommendations,
    populateTextAndBusinessNameOfflineRecommendations,
    setOfflineImageRecommendations,
    determineRecommendationSource,
    reportRecommendations,
    isOfflineRecommendationsFetched,
    finalUrl,
  ]);

  return {
    handleCopilotBannerFetch,
    handleCopilotBannerFetchSucceeded,
    handleCopilotBannerFetchFailed,
    suggestionPoller,
  };
}
